import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type GameDetail, type PlayerInGame } from '../api';
import { AppShell } from '../components/AppShell';

// Standalone stats-keeper console. Logs player counting stats
// (rebound/assist/steal/block/turnover) into the same game event
// stream the operator uses, without touching the clock or score.

interface StatEvent {
  id: string;
  eventType: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

const STAT_KEYS = ['pts', 'reb', 'ast', 'stl', 'blk', 'to'] as const;
const STAT_LABELS: Record<string, string> = {
  pts: 'PTS',
  reb: 'REB',
  ast: 'AST',
  stl: 'STL',
  blk: 'BLK',
  to: 'TO',
};

export function StatsTrackerPage() {
  const { id } = useParams<{ id: string }>();
  const [game, setGame] = useState<GameDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<{
    side: 'home' | 'away';
    playerId: string;
  } | null>(null);
  // When a shot type is picked, we wait for a Make/Miss tap.
  const [pendingShot, setPendingShot] = useState<'ft' | 'fg' | 'three' | null>(
    null,
  );
  const [events, setEvents] = useState<StatEvent[]>([]);
  const [busy, setBusy] = useState(false);
  const playerStatsRef = useRef<Record<string, Record<string, number>>>({});

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setErr(null);
      const [g, evs] = await Promise.all([
        api.getGame(id),
        api.recentStatEvents(id),
      ]);
      setGame(g);
      setEvents(evs);
      playerStatsRef.current = g.currentState?.playerStats ?? {};
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll so the operator's scoring + on-court lineup stay current here.
  useEffect(() => {
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [load]);

  async function logStat(eventType: string, extra?: Record<string, unknown>) {
    if (!id || !selected) return;
    setBusy(true);
    try {
      const res = await api.appendEvent(id, {
        eventType,
        payload: {
          side: selected.side,
          playerId: selected.playerId,
          ...(extra ?? {}),
        },
      });
      playerStatsRef.current = res.state.playerStats ?? {};
      setGame((g) => (g ? { ...g, currentState: res.state } : g));
      const evs = await api.recentStatEvents(id);
      setEvents(evs);
    } finally {
      setBusy(false);
    }
  }

  async function logShot(make: boolean) {
    if (!pendingShot) return;
    await logStat('SHOT', { shotType: pendingShot, make });
    setPendingShot(null);
  }

  async function undo(eventId: string) {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.undoEvent(id, eventId);
      setGame((g) => (g ? { ...g, currentState: res.state } : g));
      const evs = await api.recentStatEvents(id);
      setEvents(evs);
    } finally {
      setBusy(false);
    }
  }

  async function enableStats() {
    if (!id) return;
    setBusy(true);
    try {
      await api.setStatsEnabled(id, true);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!game) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  // Stats tracking can be turned off by the game admin.
  if (game.statsEnabled === false) {
    return (
      <AppShell>
        <div className="space-y-4">
          <Link to="/games" className="text-sm text-muted hover:text-text">
            ← Games
          </Link>
          <div className="card space-y-3 text-center">
            <div className="text-lg font-semibold">
              Stat tracking is off for this game
            </div>
            <p className="text-sm text-muted">
              The game admin chose not to track player stats. Turn it on to
              start logging.
            </p>
            <button
              className="btn btn-primary mx-auto"
              onClick={enableStats}
              disabled={busy}
            >
              Enable stat tracking
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  const state = game.currentState;
  const playerStats = state?.playerStats ?? {};
  const selectedTeam =
    selected?.side === 'home'
      ? game.homeTeam
      : selected?.side === 'away'
      ? game.awayTeam
      : null;
  const selectedPlayer =
    selectedTeam?.players?.find((p) => p.id === selected?.playerId) ?? null;
  const isOver = game.status === 'FINAL' || game.status === 'CANCELLED';
  const disabled = busy || isOver || !selected;

  // Reconciliation by shot type. The operator's scoring records made
  // shots by type (op_ft/op_fg2/op_3); the stats keeper's logged makes
  // are ftm, (fgm - tpm) for 2s, and tpm for 3s. A positive per-type
  // deficit means the operator scored that shot but it hasn't been
  // logged yet.
  function unloggedShots(pid: string): {
    ft: number;
    two: number;
    three: number;
    total: number;
  } {
    const s = playerStats[pid] ?? {};
    const ft = Math.max(0, (s.op_ft ?? 0) - (s.ftm ?? 0));
    const loggedTwos = (s.fgm ?? 0) - (s.tpm ?? 0);
    const two = Math.max(0, (s.op_fg2 ?? 0) - loggedTwos);
    const three = Math.max(0, (s.op_3 ?? 0) - (s.tpm ?? 0));
    return { ft, two, three, total: ft + two + three };
  }
  const allPlayers = [
    ...(game.homeTeam.players ?? []).map((p) => ({ p, team: game.homeTeam })),
    ...(game.awayTeam.players ?? []).map((p) => ({ p, team: game.awayTeam })),
  ];
  const unlogged = allPlayers
    .map(({ p, team }) => ({ p, team, shots: unloggedShots(p.id) }))
    .filter((x) => x.shots.total > 0);

  return (
    <AppShell>
      <div className="space-y-5">
        {/* Read-only game status */}
        <div>
          <Link to="/games" className="text-sm text-muted hover:text-text">
            ← Games
          </Link>
          <div className="mt-2 flex items-end justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Stats keeper
              </h1>
              <p className="mt-1 text-sm text-muted">
                {game.homeTeam.name} vs {game.awayTeam.name} · {game.sport.name}
              </p>
            </div>
            <Link
              to={`/games/${game.id}`}
              className="text-xs text-muted hover:text-text"
            >
              open operator console →
            </Link>
          </div>
        </div>

        <div className="card flex items-center justify-around gap-4 text-center">
          <ScorePill label={game.homeTeam.name} value={state?.home.score ?? 0} />
          <div>
            <div className="text-xs uppercase tracking-wider text-muted">
              {state?.intermission === 'halftime'
                ? 'Half'
                : `Period ${state?.period ?? 1}`}
            </div>
            <div className="mt-1 font-mono text-2xl font-bold">
              {state ? `${state.clock.m}:${String(state.clock.s).padStart(2, '0')}` : '—'}
            </div>
          </div>
          <ScorePill label={game.awayTeam.name} value={state?.away.score ?? 0} />
        </div>

        {isOver && (
          <div className="card text-sm text-amber-300/80">
            This game is {game.status.toLowerCase()} — stat logging is closed.
          </div>
        )}

        {/* Reconciliation banner — operator scored but shots not logged */}
        {unlogged.length > 0 && (
          <div className="rounded-lg border-2 border-yellow-400/50 bg-yellow-400/10 px-4 py-3">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-yellow-300">
              ⚠ Shots to log
            </div>
            <ul className="space-y-0.5 text-sm text-yellow-100/90">
              {unlogged.map(({ p, team, shots }) => {
                const parts: string[] = [];
                if (shots.ft) parts.push(`${shots.ft} 1pt`);
                if (shots.two) parts.push(`${shots.two} 2pt`);
                if (shots.three) parts.push(`${shots.three} 3pt`);
                return (
                  <li key={p.id}>
                    <span className="font-mono">#{p.jerseyNumber || '-'}</span>{' '}
                    <span className="font-semibold">{p.name}</span>{' '}
                    <span className="text-yellow-200/70">
                      ({team.abbreviation ?? team.name})
                    </span>{' '}
                    — log {parts.join(', ')}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Both rosters — HOME left, AWAY right */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <RosterPanel
            label="HOME"
            team={game.homeTeam}
            color={game.homeTeam.colorPrimary ?? '#3b82f6'}
            playerStats={playerStats}
            onCourt={state?.onCourt?.home ?? []}
            unloggedFor={(pid) => unloggedShots(pid).total}
            selectedPlayerId={
              selected?.side === 'home' ? selected.playerId : null
            }
            onSelect={(playerId) => {
              setPendingShot(null);
              setSelected(
                selected?.side === 'home' && selected.playerId === playerId
                  ? null
                  : { side: 'home', playerId },
              );
            }}
          />
          <RosterPanel
            label="AWAY"
            team={game.awayTeam}
            color={game.awayTeam.colorPrimary ?? '#ef4444'}
            playerStats={playerStats}
            onCourt={state?.onCourt?.away ?? []}
            unloggedFor={(pid) => unloggedShots(pid).total}
            selectedPlayerId={
              selected?.side === 'away' ? selected.playerId : null
            }
            onSelect={(playerId) => {
              setPendingShot(null);
              setSelected(
                selected?.side === 'away' && selected.playerId === playerId
                  ? null
                  : { side: 'away', playerId },
              );
            }}
          />
        </div>

        {/* Stat buttons */}
        <div className="card space-y-3">
          <div className="text-xs uppercase tracking-wider text-muted">
            {selectedPlayer && selectedTeam ? (
              <>
                Logging for{' '}
                <span className="text-text">
                  #{selectedPlayer.jerseyNumber || '-'}{' '}
                  {selectedPlayer.name}
                </span>{' '}
                <span className="text-muted">
                  ({selectedTeam.abbreviation ?? selectedTeam.name})
                </span>
              </>
            ) : (
              'Tap a player on either team, then a stat'
            )}
          </div>

          {/* Live shooting line for the selected player */}
          {selectedPlayer && (
            <div className="flex flex-wrap gap-4 rounded-md border border-border bg-surface px-3 py-2 font-mono text-xs">
              {(() => {
                const s = playerStats[selectedPlayer.id] ?? {};
                return (
                  <>
                    <ShootingLine label="FG" m={s.fgm} a={s.fga} />
                    <ShootingLine label="3FG" m={s.tpm} a={s.tpa} />
                    <ShootingLine label="FT" m={s.ftm} a={s.fta} />
                  </>
                );
              })()}
            </div>
          )}

          {selectedPlayer ? (
            <>
              {/* Shots — pick a type, then Make/Miss */}
              <div>
                <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted">
                  Shots
                </div>
                {pendingShot ? (
                  <div className="space-y-2">
                    <div className="text-sm font-semibold">
                      {pendingShot === 'ft'
                        ? 'Free throw (+1)'
                        : pendingShot === 'fg'
                        ? 'Shot (+2)'
                        : 'Three (+3)'}{' '}
                      — make or miss?
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <StatBtn
                        label="✓ Make"
                        onClick={() => logShot(true)}
                        disabled={disabled}
                      />
                      <StatBtn
                        tone="miss"
                        label="✗ Miss"
                        onClick={() => logShot(false)}
                        disabled={disabled}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingShot(null)}
                      className="text-xs text-muted hover:text-text"
                    >
                      ← back to shot type
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <StatBtn
                      label="Free throw (+1)"
                      onClick={() => setPendingShot('ft')}
                      disabled={disabled}
                    />
                    <StatBtn
                      label="Shot (+2)"
                      onClick={() => setPendingShot('fg')}
                      disabled={disabled}
                    />
                    <StatBtn
                      label="Three (+3)"
                      onClick={() => setPendingShot('three')}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>

              {/* Counting stats */}
              <div>
                <div className="mb-1.5 text-[10px] uppercase tracking-wider text-muted">
                  Other stats
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                  <StatBtn label="Rebound — DEF" onClick={() => logStat('REBOUND', { off: false })} disabled={disabled} />
                  <StatBtn label="Rebound — OFF" onClick={() => logStat('REBOUND', { off: true })} disabled={disabled} />
                  <StatBtn label="Assist" onClick={() => logStat('ASSIST')} disabled={disabled} />
                  <StatBtn label="Steal" onClick={() => logStat('STEAL')} disabled={disabled} />
                  <StatBtn label="Block" onClick={() => logStat('BLOCK')} disabled={disabled} />
                  <StatBtn label="Turnover" onClick={() => logStat('TURNOVER')} disabled={disabled} />
                </div>
              </div>
              <p className="text-[11px] text-muted">
                The score stays with the clock operator — logging a make
                here records shooting %, it does not change the score.
              </p>
            </>
          ) : null}
        </div>

        {/* Team totals */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <TeamTotals
            label={game.homeTeam.name}
            players={game.homeTeam.players ?? []}
            playerStats={playerStats}
          />
          <TeamTotals
            label={game.awayTeam.name}
            players={game.awayTeam.players ?? []}
            playerStats={playerStats}
          />
        </div>

        {/* Recent events log */}
        <div className="card">
          <div className="mb-2 text-xs uppercase tracking-wider text-muted">
            Recent stat events
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-muted">No stats logged yet.</p>
          ) : (
            <ul className="space-y-1">
              {events.map((e) => (
                <EventRow
                  key={e.id}
                  event={e}
                  game={game}
                  onUndo={() => undo(e.id)}
                  disabled={busy}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function ScorePill({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-xs uppercase tracking-wider text-muted">
        {label}
      </div>
      <div className="mt-1 font-mono text-4xl font-bold">{value}</div>
    </div>
  );
}

function pct(m: number, a: number): string {
  return a > 0 ? `${Math.round((m / a) * 100)}%` : '—';
}

function TeamTotals({
  label,
  players,
  playerStats,
}: {
  label: string;
  players: PlayerInGame[];
  playerStats: Record<string, Record<string, number>>;
}) {
  // Sum each counter across the team's roster.
  const t = players.reduce(
    (acc, p) => {
      const s = playerStats[p.id] ?? {};
      for (const k of [
        'fgm',
        'fga',
        'tpm',
        'tpa',
        'ftm',
        'fta',
        'reb',
        'oreb',
        'dreb',
        'ast',
        'stl',
        'blk',
        'to',
      ]) {
        acc[k] = (acc[k] ?? 0) + (s[k] ?? 0);
      }
      return acc;
    },
    {} as Record<string, number>,
  );
  const cells: Array<[string, string]> = [
    ['FG', `${t.fgm ?? 0}-${t.fga ?? 0} (${pct(t.fgm ?? 0, t.fga ?? 0)})`],
    ['3FG', `${t.tpm ?? 0}-${t.tpa ?? 0} (${pct(t.tpm ?? 0, t.tpa ?? 0)})`],
    ['FT', `${t.ftm ?? 0}-${t.fta ?? 0} (${pct(t.ftm ?? 0, t.fta ?? 0)})`],
    ['REB', `${t.reb ?? 0} (${t.oreb ?? 0}o/${t.dreb ?? 0}d)`],
    ['AST', String(t.ast ?? 0)],
    ['STL', String(t.stl ?? 0)],
    ['BLK', String(t.blk ?? 0)],
    ['TO', String(t.to ?? 0)],
  ];
  return (
    <div className="card">
      <div className="mb-2 truncate text-xs uppercase tracking-wider text-muted">
        {label} — team totals
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
        {cells.map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-wider text-muted">
              {k}
            </div>
            <div className="font-mono text-sm font-semibold">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface PanelTeam {
  id: string;
  name: string;
  abbreviation: string | null;
  colorPrimary: string | null;
  players?: PlayerInGame[];
}

function RosterPanel({
  label,
  team,
  color,
  playerStats,
  onCourt,
  unloggedFor,
  selectedPlayerId,
  onSelect,
}: {
  label: string;
  team: PanelTeam;
  color: string;
  playerStats: Record<string, Record<string, number>>;
  onCourt: string[];
  unloggedFor: (playerId: string) => number;
  selectedPlayerId: string | null;
  onSelect: (playerId: string) => void;
}) {
  const players = team.players ?? [];
  const onCourtSet = new Set(onCourt);
  // When no lineup has been set yet, treat everyone as "in game" so
  // nothing is greyed out until the operator picks a lineup.
  const lineupSet = onCourt.length > 0;
  // On-court players first (in lineup order), then the bench.
  const sorted = lineupSet
    ? [
        ...onCourt
          .map((id) => players.find((p) => p.id === id))
          .filter(Boolean) as PlayerInGame[],
        ...players.filter((p) => !onCourtSet.has(p.id)),
      ]
    : players;

  return (
    <div className="card p-0">
      <div className="flex items-center gap-3 border-b border-border px-3 py-2.5">
        <span className="h-3 w-3 rounded-full" style={{ background: color }} />
        <span className="text-[10px] uppercase tracking-wider text-muted">
          {label}
        </span>
        <span className="truncate text-sm font-bold">{team.name}</span>
        {lineupSet && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-300">
            {onCourt.length} on court
          </span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_repeat(6,30px)] gap-0.5 border-b border-border bg-white/5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted">
        <span>Player</span>
        {STAT_KEYS.map((k) => (
          <span key={k} className="text-center">
            {STAT_LABELS[k]}
          </span>
        ))}
      </div>
      {sorted.length === 0 ? (
        <div className="px-3 py-4 text-sm text-muted">
          No players on this team's roster.
        </div>
      ) : (
        sorted.map((p) => (
          <RosterRow
            key={p.id}
            player={p}
            stats={playerStats[p.id]}
            selected={selectedPlayerId === p.id}
            inGame={!lineupSet || onCourtSet.has(p.id)}
            unlogged={unloggedFor(p.id)}
            onSelect={() => onSelect(p.id)}
          />
        ))
      )}
    </div>
  );
}

function RosterRow({
  player,
  stats,
  selected,
  inGame,
  unlogged,
  onSelect,
}: {
  player: PlayerInGame;
  stats?: Record<string, number>;
  selected: boolean;
  inGame: boolean;
  unlogged: number;
  onSelect: () => void;
}) {
  const flagged = unlogged > 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`grid w-full grid-cols-[1fr_repeat(6,30px)] items-center gap-0.5 border-b border-white/5 px-3 py-2.5 text-left transition ${
        selected
          ? 'bg-brand/20'
          : flagged
          ? 'bg-yellow-400/15 hover:bg-yellow-400/25'
          : inGame
          ? 'hover:bg-white/5'
          : 'opacity-40 hover:opacity-70'
      }`}
    >
      <span className="flex items-center gap-2 truncate">
        <span
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
            inGame ? 'bg-emerald-400' : 'bg-transparent'
          }`}
          title={inGame ? 'On court' : 'On bench'}
        />
        <span className="font-mono text-sm text-muted">
          #{player.jerseyNumber || '-'}
        </span>
        <span
          className={`truncate text-sm font-semibold ${
            flagged ? 'text-yellow-200' : inGame ? '' : 'text-muted'
          }`}
        >
          {player.name}
        </span>
        {flagged && (
          <span
            className="ml-1 shrink-0 rounded bg-yellow-400 px-1 text-[10px] font-bold text-black"
            title={`${unlogged} shot(s) to log`}
          >
            {unlogged} to log
          </span>
        )}
      </span>
      {STAT_KEYS.map((k) => (
        <span
          key={k}
          className={`text-center font-mono text-sm ${
            (stats?.[k] ?? 0) > 0 ? 'text-text' : 'text-muted/40'
          }`}
        >
          {stats?.[k] ?? 0}
        </span>
      ))}
    </button>
  );
}

function StatBtn({
  label,
  onClick,
  disabled,
  tone = 'stat',
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  tone?: 'stat' | 'miss';
}) {
  const styles =
    tone === 'miss'
      ? 'border-red-400/40 bg-red-400/5 text-red-300 hover:bg-red-400/15 active:bg-red-400/25'
      : 'border-emerald-400/40 bg-emerald-400/5 text-emerald-300 hover:bg-emerald-400/15 active:bg-emerald-400/25';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg border-2 px-3 py-4 text-sm font-bold transition disabled:cursor-not-allowed disabled:border-border disabled:text-muted/40 disabled:hover:bg-transparent ${styles}`}
    >
      {label}
    </button>
  );
}

function ShootingLine({
  label,
  m,
  a,
}: {
  label: string;
  m?: number;
  a?: number;
}) {
  const made = m ?? 0;
  const att = a ?? 0;
  const pct = att > 0 ? Math.round((made / att) * 100) : null;
  return (
    <span className="text-muted">
      <span className="text-[10px] uppercase tracking-wider">{label}</span>{' '}
      <span className="text-text">
        {made}-{att}
      </span>
      {pct !== null && <span className="text-muted/70"> ({pct}%)</span>}
    </span>
  );
}

function EventRow({
  event,
  game,
  onUndo,
  disabled,
}: {
  event: StatEvent;
  game: GameDetail;
  onUndo: () => void;
  disabled: boolean;
}) {
  const payload = event.payload ?? {};
  const side = payload.side === 'away' ? 'away' : 'home';
  const team = side === 'home' ? game.homeTeam : game.awayTeam;
  const player = team.players?.find((p) => p.id === payload.playerId);
  const off = payload.off === true;
  const shotType = payload.shotType as string | undefined;
  const make = payload.make === true;
  const SHOT_LABEL: Record<string, string> = {
    ft: 'FT',
    fg: '2PT',
    three: '3PT',
  };
  let label: string;
  if (event.eventType === 'SHOT') {
    label = `${SHOT_LABEL[shotType ?? ''] ?? 'Shot'} ${make ? 'make' : 'miss'}`;
  } else if (event.eventType === 'REBOUND') {
    label = `Rebound (${off ? 'OFF' : 'DEF'})`;
  } else {
    const LABELS: Record<string, string> = {
      ASSIST: 'Assist',
      STEAL: 'Steal',
      BLOCK: 'Block',
      TURNOVER: 'Turnover',
    };
    label = LABELS[event.eventType] ?? event.eventType;
  }
  const isMiss = event.eventType === 'SHOT' && !make;
  return (
    <li className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-1.5 text-sm">
      <span>
        <span className={`font-semibold ${isMiss ? 'text-red-300' : ''}`}>
          {label}
        </span>
        <span className="text-muted">
          {' '}
          · {player ? `#${player.jerseyNumber || '-'} ${player.name}` : '—'} ·{' '}
          {team.abbreviation ?? team.name}
        </span>
      </span>
      <button
        type="button"
        onClick={onUndo}
        disabled={disabled}
        className="text-xs text-red-300 hover:underline disabled:opacity-50"
      >
        Undo
      </button>
    </li>
  );
}
